import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "reactstrap";

import apiPortalTransportador from "../../../services/apiPortalTransportador";
import { useToast } from "../../../hooks/toast";
import { useAuth } from "../../../hooks/auth";
import { useCompanies } from "hooks/companies";
import SpecificEmpresaRequiredNotice from "../../../components/SpecificEmpresaRequiredNotice";
import PageHero, { PageHeroRefreshButton } from "../../../components/PageHero";
import ShellCard from "../../../components/ShellCard";
import { AUTOPILOT_COLORS } from "../../../components/autopilot/styles";

import { CANAIS, URL_ULTIMOS_ENVIOS } from "./canais";
import {
  MAX_ARQUIVOS,
  MAX_TOTAL_BYTES,
  getEmailFromUser,
  parseUploadError,
} from "./helpers";
import FormularioEnvio from "./components/FormularioEnvio";
import ResultadoEnvio from "./components/ResultadoEnvio";
import UltimosEnviosCard from "./components/UltimosEnviosCard";

/**
 * Tela de Upload de Arquivos do Hub, um componente para os DOIS canais
 * (PO1-2716). `canal="embarcador"` é a tela que já estava em produção
 * (PO1-2706); `canal="transportador"` é a mesma coisa apontando para o par
 * embarcador x transportadora — antes era um modal com .zip único.
 *
 * O que muda entre os canais está todo em `canais.js`: textos, endpoints, se
 * exige transportadora selecionada e os ids de DOM.
 */
export default function UploadArquivosCanal({ canal }) {
  const cfg = CANAIS[canal];
  const { addToast } = useToast();
  const { user } = useAuth();
  const {
    empresa,
    transportadora,
    hasEmpresaSelection,
    hasTransportadoraSelection,
    isAllEmpresas,
  } = useCompanies();
  const empresaId = empresa?.value;
  // PO1-2310: no canal do MeuFrete a transportadora é a do login; a URL dos
  // tipos leva o id, mas o servidor ignora e usa o JWT.
  const transportadoraId = cfg.transportadoraFixa ? user?.transportadoraId : transportadora?.value;
  const userEmail = getEmailFromUser(user);

  const [loadingTipos, setLoadingTipos] = useState(false);
  const [tipos, setTipos] = useState([]);
  const [selectedTipo, setSelectedTipo] = useState(null);

  const [arquivos, setArquivos] = useState([]);
  const [notificarEmail, setNotificarEmail] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [resultadoEnvio, setResultadoEnvio] = useState(null);

  const [envios, setEnvios] = useState([]);
  const [loadingEnvios, setLoadingEnvios] = useState(false);

  // O canal do transportador só faz sentido com o par embarcador x
  // transportadora resolvido — é ele que define a pasta de destino.
  const contextoPronto = useMemo(
    () =>
      cfg.transportadoraFixa
        ? hasEmpresaSelection && !isAllEmpresas && Boolean(transportadoraId)
        : hasEmpresaSelection && (!cfg.exigeTransportadora || hasTransportadoraSelection),
    [cfg.exigeTransportadora, cfg.transportadoraFixa, hasEmpresaSelection, hasTransportadoraSelection, isAllEmpresas, transportadoraId]
  );

  const loadTipos = useCallback(async () => {
    if (!contextoPronto) {
      setSelectedTipo(null);
      setTipos([]);
      return;
    }
    setSelectedTipo(null);
    setTipos([]);
    try {
      setLoadingTipos(true);
      const response = await apiPortalTransportador.get(
        cfg.urlTipos(empresaId, transportadoraId)
      );
      if (response.status === 200 && response.data.length > 0) {
        setTipos(response.data.map((item) => ({ value: item.Id, label: item.Nome })));
      }
    } catch (error) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Error",
        description:
          "Erro ao buscar tipos! : " + (error.response?.data ? error.response.data : error),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setLoadingTipos(false);
    }
  }, [cfg, empresaId, transportadoraId, addToast, contextoPronto]);

  const loadUltimosEnvios = useCallback(async () => {
    if (!contextoPronto || !empresaId) {
      setEnvios([]);
      return;
    }
    try {
      setLoadingEnvios(true);
      const response = await apiPortalTransportador.get(`${URL_ULTIMOS_ENVIOS}/${empresaId}`, {
        params: {
          idTipoInformacao: selectedTipo?.value,
          idTransportador: transportadoraId,
          canal: cfg.canal,
        },
      });
      setEnvios(response.data?.envios || []);
    } catch (error) {
      // Falha ao carregar histórico não deve travar o fluxo de envio.
    } finally {
      setLoadingEnvios(false);
    }
  }, [cfg, empresaId, contextoPronto, selectedTipo, transportadoraId]);

  useEffect(() => {
    loadTipos();
  }, [loadTipos]);

  useEffect(() => {
    loadUltimosEnvios();
  }, [loadUltimosEnvios]);

  const handleSelectTipo = (value) => {
    setSelectedTipo(value);
    setArquivos([]);
    setResultadoEnvio(null);
  };

  const handleFilesChosen = (event) => {
    const chosen = Array.from(event.target.files || []);
    if (!chosen.length) return;

    const merged = [...arquivos];
    const duplicados = [];
    chosen.forEach((file) => {
      if (merged.some((f) => f.name === file.name && f.size === file.size)) {
        duplicados.push(file.name);
      } else {
        merged.push(file);
      }
    });

    // Antes o duplicado era descartado em silêncio e o usuário achava que tinha
    // selecionado a mais (H1: "aviso da recusa por nome duplicado").
    if (duplicados.length) {
      addToast({
        place: "tr",
        type: "info",
        title: "Arquivo já estava na lista",
        description: duplicados.join(", "),
        icon: "tim-icons icon-alert-circle-exc",
      });
    }

    if (merged.length > MAX_ARQUIVOS) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Limite excedido",
        description: `Máximo de ${MAX_ARQUIVOS} arquivos por envio. Zipe os arquivos e tente novamente.`,
        icon: "tim-icons icon-alert-circle-exc",
      });
      event.target.value = "";
      return;
    }

    const mergedBytes = merged.reduce((acc, file) => acc + file.size, 0);
    if (mergedBytes > MAX_TOTAL_BYTES) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Limite excedido",
        description: "Envio acima de 10 MB. Zipe os arquivos e tente novamente.",
        icon: "tim-icons icon-alert-circle-exc",
      });
      event.target.value = "";
      return;
    }

    setArquivos(merged);
    event.target.value = "";
  };

  const handleRemoveArquivo = (index) => {
    setArquivos((current) => current.filter((_, i) => i !== index));
  };

  const handleNovoEnvio = () => {
    setArquivos([]);
    setResultadoEnvio(null);
  };

  const handleEnviar = async () => {
    if (!arquivos.length || !selectedTipo) return;

    setEnviando(true);
    try {
      const formData = new FormData();
      arquivos.forEach((file) => formData.append("Arquivos", file));
      if (empresaId !== undefined && empresaId !== null) {
        formData.append("idEmbarcador", empresaId);
      }
      if (transportadoraId !== undefined && transportadoraId !== null) {
        formData.append("idTransportador", transportadoraId);
      }
      formData.append("idTipoInformacao", selectedTipo.value);
      formData.append("notificarEmail", notificarEmail);

      const response = await apiPortalTransportador.post(cfg.urlPost, formData, {
        headers: { "content-type": "multipart/form-data" },
      });

      setResultadoEnvio(response.data);
      setArquivos([]);
      addToast({
        place: "tr",
        type: "success",
        title: "Entregue na fila",
        description: "Arquivos entregues na fila de processamento.",
        icon: "tim-icons icon-check-2",
      });
      loadUltimosEnvios();
    } catch (error) {
      const parsedError = parseUploadError(error);
      // PO1-2310: vínculo ativo sem canal configurado.
      if (error?.response?.data?.code === "CANAL_NAO_CONFIGURADO") {
        parsedError.title = "Canal não configurado";
        parsedError.description = error.response.data.message;
      }
      const hasDetails = parsedError.details.length > 0;
      addToast({
        place: "tr",
        type: "danger",
        title: parsedError.title,
        description: (
          <div style={{ textAlign: "left" }}>
            <p style={{ marginBottom: hasDetails ? 6 : 0 }}>{parsedError.description}</p>
            {hasDetails ? (
              <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                {parsedError.details.map((item, index) => (
                  <li key={`${index}-${item}`}>{item}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ),
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setEnviando(false);
    }
  };

  if (!contextoPronto) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice message={cfg.mensagemGate} />
      </div>
    );
  }

  return (
    <div className="content">
      <Container fluid>
        <PageHero
          eyebrow={cfg.eyebrow}
          title={cfg.titulo}
          description={cfg.descricao}
          actions={
            <PageHeroRefreshButton onClick={loadUltimosEnvios} loading={loadingEnvios} />
          }
        >
          <div
            style={{
              color: AUTOPILOT_COLORS.muted,
              fontSize: "0.78rem",
              marginTop: 10,
            }}
          >
            Destino:{" "}
            <strong style={{ color: AUTOPILOT_COLORS.title }}>{empresa?.label}</strong>
            {cfg.transportadoraFixa
              ? user?.transportadoraNome ? ` · ${user.transportadoraNome}` : ""
              : transportadora?.label ? ` · ${transportadora.label}` : ""}
          </div>
        </PageHero>

        <Row>
          <Col md={7}>
            <ShellCard title="Envio">
              {resultadoEnvio ? (
                <ResultadoEnvio resultado={resultadoEnvio} onNovoEnvio={handleNovoEnvio} />
              ) : (
                <FormularioEnvio
                  cfg={cfg}
                  tipos={tipos}
                  loadingTipos={loadingTipos}
                  selectedTipo={selectedTipo}
                  onSelectTipo={handleSelectTipo}
                  arquivos={arquivos}
                  onFilesChosen={handleFilesChosen}
                  onRemoveArquivo={handleRemoveArquivo}
                  notificarEmail={notificarEmail}
                  onNotificarEmail={setNotificarEmail}
                  userEmail={userEmail}
                  enviando={enviando}
                  onEnviar={handleEnviar}
                />
              )}
            </ShellCard>
          </Col>

          <Col md={5}>
            <div style={{ marginTop: "1.25rem" }}>
              <UltimosEnviosCard
                envios={envios}
                loading={loadingEnvios}
                onAtualizar={loadUltimosEnvios}
              />
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
